import type { StudyMissionContextDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { LibraryBigIcon } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';

export type MeiaTemplateOption = {
  description?: string | null;
  id: string;
  missionContext?: StudyMissionContextDto | null;
  name: string;
};

type MeiaTemplatePopoverProps = {
  className?: string;
  onSelect: (template: MeiaTemplateOption) => void;
  renderTemplate?: (template: MeiaTemplateOption) => ReactNode;
  templates: MeiaTemplateOption[];
};

const defaultRender = (template: MeiaTemplateOption): ReactNode => (
  <>
    <div className="text-sm font-medium text-gray-950">{template.name}</div>
    {template.description && <p className="mt-1 line-clamp-2 text-xs text-gray-600">{template.description}</p>}
  </>
);

export const MeiaTemplatePopover: FC<MeiaTemplatePopoverProps> = (props) => {
  const { className, onSelect, renderTemplate = defaultRender, templates } = props;
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (template: MeiaTemplateOption): void => {
    onSelect(template);
    setIsOpen(false);
  };

  return (
    <div className={className}>
      <Popover
        onOpenChange={setIsOpen}
        open={isOpen}
      >
        <TooltipComponent
          side="top"
          trigger={
            <PopoverTrigger asChild>
              <IconButton
                ariaLabel="Pick from Library"
                icon={<LibraryBigIcon />}
                variant="outline"
              />
            </PopoverTrigger>
          }
        >
          Pick from Library
        </TooltipComponent>
        <PopoverContent
          align="end"
          className="w-96 p-0"
          side="bottom"
        >
          <div className="max-h-80 overflow-y-auto">
            {templates.length === 0 ? (
              <div className="p-3 text-xs text-gray-500">No templates available.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {templates.map((template) => (
                  <li key={template.id}>
                    <button
                      className="w-full px-3 py-3 text-left transition-colors hover:bg-gray-50"
                      onClick={() => handleSelect(template)}
                      type="button"
                    >
                      {renderTemplate(template)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
